import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useLocalizedCountries } from '@/hooks/useLocalizedCountries';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyH5, TypographyP2 } from '@heidi/design-system/typography';
import { TemplateFilterBy } from '@heidi/schema/template-gallery';
import { FilterSettings } from '@heidi/templates/contracts';
import { Blocks } from 'lucide-react';
import { type ReactNode } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import {
  getTemplateCategoryFilterLabel,
  isTemplateCategoryFilterKey,
} from './communityFilters.messages';

const ALL = String(FilterSettings.ALL);
const UNDEFINED = String(FilterSettings.UNDEFINED);
const isFilterAll = (value?: string) => !value || value === ALL || value === UNDEFINED;

// Maps the backend `disabled_filters` array to a copy variant; 'fallback'
// covers anything un-enumerated (most often an empty array from a text-search miss).
type DisabledFiltersCase =
  | 'specialty'
  | 'country'
  | 'specialty+content'
  | 'all'
  | 'fallback';

const resolveDisabledFiltersCase = (
  disabledFilters?: TemplateFilterBy[]
): DisabledFiltersCase => {
  if (!disabledFilters) return 'fallback';
  const has = (k: TemplateFilterBy) => disabledFilters.includes(k);
  const size = disabledFilters.length;
  if (size === 1 && has(TemplateFilterBy.specialty)) return 'specialty';
  if (size === 1 && has(TemplateFilterBy.country)) return 'country';
  if (
    size === 2 &&
    has(TemplateFilterBy.specialty) &&
    has(TemplateFilterBy.clinical_content_group)
  ) {
    return 'specialty+content';
  }
  if (
    size === 3 &&
    has(TemplateFilterBy.specialty) &&
    has(TemplateFilterBy.country) &&
    has(TemplateFilterBy.clinical_content_group)
  ) {
    return 'all';
  }
  return 'fallback';
};

const resolveHeading = (
  resolvedCase: DisabledFiltersCase,
  intl: IntlShape,
  location: string,
  formattedSpecialty: string,
  formattedCountry: string
) => {
  switch (resolvedCase) {
    case 'country':
      return intl.formatMessage(
        {
          defaultMessage: 'Showing results from {specialty} worldwide:',
          id: 'Y0Zo+PH4XS',
        },
        { specialty: formattedSpecialty }
      );
    case 'specialty+content':
      return isFilterAll(location)
        ? intl.formatMessage({
            defaultMessage: 'Showing results from all specialties:',
            id: 'OyFpsyAZYr',
          })
        : intl.formatMessage(
            {
              defaultMessage: 'Showing results from all specialties in {country}:',
              id: 'TVZZW1aKua',
            },
            { country: formattedCountry }
          );
    case 'all':
      return intl.formatMessage({
        defaultMessage: 'Explore diverse templates from our community:',
        id: 'Yr2pXtZf0O',
      });
    // 'specialty' and 'fallback' share the generic lead-in.
    case 'specialty':
    case 'fallback':
    default:
      return intl.formatMessage({
        defaultMessage: 'Showing related templates:',
        id: 'NvZJUGcyBF',
      });
  }
};

const RecommendedTemplatesNonEmpty = ({
  disabledFilters,
  isRelatedSection = false,
}: {
  disabledFilters?: TemplateFilterBy[];
  isRelatedSection?: boolean;
}) => {
  const { specialty, location, category, search } = useUrlFilters();
  const { getLocalizedNameFromEnglish } = useLocalizedCountries();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);
  const intl = useIntl();

  const resolvedCase = resolveDisabledFiltersCase(disabledFilters);

  const formattedSpecialty = isFilterAll(specialty)
    ? intl.formatMessage({ defaultMessage: 'any specialty', id: '38G2aEYq2E' })
    : (
        specialties?.find((entry) => entry.name === specialty)?.localizedName ?? specialty
      ).toLowerCase();

  const formattedCountry = isFilterAll(location)
    ? intl.formatMessage({ defaultMessage: 'worldwide', id: 'WmK+HsQiBc' })
    : getLocalizedNameFromEnglish(location);

  const heading = isRelatedSection
    ? intl.formatMessage({
        defaultMessage: 'Showing related templates:',
        id: 'NvZJUGcyBF',
      })
    : resolveHeading(resolvedCase, intl, location, formattedSpecialty, formattedCountry);

  let body: ReactNode = null;
  if (isRelatedSection) {
    body = null;
  } else if (resolvedCase === 'fallback') {
    if (search) {
      body = intl.formatMessage({
        defaultMessage: 'No results for that search',
        id: 'lcK+vyxUSX',
      });
    }
  } else {
    const normalizedCategory = String(category);
    const hasCategory = normalizedCategory !== ALL && normalizedCategory !== UNDEFINED;
    const formattedCategory =
      hasCategory && isTemplateCategoryFilterKey(category)
        ? getTemplateCategoryFilterLabel(intl, category).toLowerCase()
        : '';

    body = isFilterAll(location)
      ? intl.formatMessage(
          {
            defaultMessage:
              'There are no {hasCategory, select, true {{category} templates} other {templates}} from {specialty} worldwide yet',
            id: 'XCB9X6QMJf',
          },
          {
            hasCategory: hasCategory ? 'true' : 'other',
            category: formattedCategory,
            specialty: formattedSpecialty,
          }
        )
      : intl.formatMessage(
          {
            defaultMessage:
              'There are no {hasCategory, select, true {{category} templates} other {templates}} from {specialty} in {country} yet',
            id: '+n7OkL+nOs',
          },
          {
            hasCategory: hasCategory ? 'true' : 'other',
            category: formattedCategory,
            specialty: formattedSpecialty,
            country: formattedCountry,
          }
        );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <FramedIcon size="lg" icon={<Blocks />} />
      <div className="flex flex-col gap-2.5 text-center">
        {body !== null && (
          <TypographyP2 className="text-text-secondary">{body}</TypographyP2>
        )}
        <TypographyH5>{heading}</TypographyH5>
      </div>
    </div>
  );
};

export default RecommendedTemplatesNonEmpty;
